import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as countryActions from "./../country/countryActions";
import * as authSelectors from "./../auth/authSelectors";
import * as _ from "lodash";
import { colors } from "../../components/colors";
import { PrimaryButton } from "../../components/Buttons";
import { FormRow } from "../common/hook-form";
import { UserStatus } from "../../model/UserStatus";
import { useIsWarner } from "../../util";
import { CardHeader } from "../../components/CardHeader";
import { FormProvider, useForm } from "react-hook-form";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { useGetMembercardTypes } from "../../service/api";
import { useHandleFetchError } from "../../error-handler";

interface OwnProps {
  onSubmit: Function;
  onReset: Function;
  currentFilters: any;
  isStatusFilterVisible: boolean;
  isCountryFilterVisible: boolean;
  isNameOrEmailFilterVisible: boolean;
  isPhoneNbFilterVisible: boolean;
}

const genderOptions = [
  {
    label: "Any",
    value: "",
  },
  {
    label: "Male",
    value: 1,
  },
  {
    label: "Female",
    value: 2,
  },
];

export const UserListFiltersForm: React.FC<OwnProps> = (props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const isWarner = useIsWarner();

  const {
    data: memberCardTypes,
    error: memberCardTypesError,
  } = useGetMembercardTypes(organizationKeyName);

  const dispatch = useDispatch();
  const methods = useForm({
    defaultValues: {
      ...props.currentFilters,
    },
  });

  React.useEffect(() => {
    dispatch(countryActions.listCountries());
  }, [dispatch, organizationKeyName]);

  useHandleFetchError(memberCardTypesError);

  if (
    !props.isStatusFilterVisible &&
    !props.isCountryFilterVisible &&
    !props.isPhoneNbFilterVisible &&
    !props.isNameOrEmailFilterVisible
  ) {
    return null;
  }

  const memberCardTypeOptions = _.chain([...(memberCardTypes || [])])
    .map((x) => ({
      label: x.name,
      value: x.id,
    }))
    .value();

  memberCardTypeOptions.unshift({
    label: "Any",
    value: "",
  });

  const approvalOptions = [
    {
      label: "Any status",
      value: "",
    },
    {
      label: "Accepted",
      value: UserStatus.Accepted,
    },
    {
      label: "Denied",
      value: UserStatus.Denied,
    },
    {
      label: "Pending",
      value: UserStatus.Pending,
    },
  ];

  const hasAcceptedMarketingOptions = [
    {
      label: "Any",
      value: "",
    },
    {
      label: "Yes",
      value: "true",
    },
    {
      label: "No",
      value: "false",
    },
  ];

  const handleSubmit = (values: { [key: string]: any }) => {
    props.onSubmit(_.omitBy(values, (x) => x === ""));
  };

  return (
    /* @ts-ignore */
    <FormProvider {...methods}>
      <form className="card" onSubmit={methods.handleSubmit(handleSubmit)}>
        <CardHeader title="Filter user list" />
        <div className="card-body d-flex flex-column" style={{ gap: "16px" }}>
          <FormRow>
            {props.isNameOrEmailFilterVisible && (
              <TextInput
                name="filterByNameOrEmail"
                label="Name or Email"
                placeholder="Filter by name or email"
              />
            )}
            {props.isPhoneNbFilterVisible && (
              <TextInput
                name="filterByPhoneNumber"
                label="Phone number"
                placeholder="Filter by phone number"
              />
            )}
            <SelectInput
              name="filterByHasAcceptedMarketing"
              label="Accepted marketing"
              options={hasAcceptedMarketingOptions}
            />
          </FormRow>
          <FormRow>
            <SelectInput
              name="filterByGender"
              label="Gender (or guessed gender)"
              options={genderOptions}
            />
            {isWarner && (
              <SelectInput
                name="filterByApproval"
                label="Approval state"
                options={approvalOptions}
              />
            )}
          </FormRow>
          <FormRow>
            <SelectInput
              name="filterByMemberCardType"
              label="Member card type"
              options={memberCardTypeOptions}
            />
          </FormRow>
          <FormRow>
            <SelectInput
              name="filterByUserRole"
              label="User role"
              // Iterating over Roles enum: https://blog.logrocket.com/iterate-over-enums-typescript/
              options={[
                { label: "None", value: "" },
                { label: "Staff tablet access", value: "ROLE_SHOP_TERMINAL" },
                { label: "Challenges access", value: "ROLE_CHALLENGES" },
                { label: "Admin access", value: "ROLE_NIGHTPAY_ADMIN" },
              ]}
            />
          </FormRow>
        </div>
        <div className="card-footer d-flex pt-0 border-top-0 justify-content-between">
          <PrimaryButton
            backgroundColor={colors.transparent}
            hoverColor={colors.ctaLight}
            color={colors.ctaMain}
            type="reset"
            onClick={(e) => {
              methods.reset();
              props.onReset(e);
            }}
          >
            Reset
          </PrimaryButton>
          <PrimaryButton
            backgroundColor={colors.ctaMain}
            hoverColor={colors.ctaSecondaryOne}
            color={colors.white}
            type="submit"
          >
            Search
          </PrimaryButton>
        </div>
      </form>
    </FormProvider>
  );
};

export default UserListFiltersForm;
